import * as THREE from 'three';

// ============================================================
// Drift Sparks -- Mario Kart-style color-progressing spark VFX
// ============================================================

/** Color thresholds for drift duration -- HDR values (> 1.0) for bloom pickup */
const DRIFT_ORANGE = new THREE.Color(2.0, 0.7, 0.0);   // HDR warm orange
const DRIFT_RED    = new THREE.Color(2.5, 0.1, 0.0);   // HDR hot red
const DRIFT_PURPLE = new THREE.Color(1.5, 0.0, 3.0);   // HDR electric purple

/** LDR versions for MeshStandardMaterial (which doesn't support HDR color directly) */
const STREAK_ORANGE = new THREE.Color(1.0, 0.45, 0.0);
const STREAK_RED    = new THREE.Color(1.0, 0.1, 0.0);
const STREAK_PURPLE = new THREE.Color(0.6, 0.0, 1.0);

const ORANGE_THRESHOLD = 1.5; // seconds
const RED_THRESHOLD    = 3.0; // seconds

/** Max concurrent spark particles per kart */
const MAX_PARTICLES = 50;

/** Spark particle lifetime range (seconds) */
const SPARK_LIFETIME_MIN = 0.2;
const SPARK_LIFETIME_MAX = 0.45;

/** How many sparks to spawn per second while drifting */
const SPAWN_RATE = 80;

/** Individual spark particle state */
interface Spark {
  age: number;
  lifetime: number;
  // Local-space velocity (relative to kart at spawn time, but we detach to world)
  vx: number;
  vy: number;
  vz: number;
}

/**
 * Manages drift spark particle effects for a single kart.
 *
 * Uses a THREE.Points system with a custom ShaderMaterial for efficient
 * GPU-rendered additive-blended sparks with per-particle color and opacity.
 * Sparks are rendered as elongated motion-blur streaks with a bright HDR core
 * and soft glow falloff for bloom pickup.
 *
 * Also includes two small ground-streak emissive planes under the rear wheels,
 * each paired with a wider outer-glow plane for a tiered brightness look.
 */
export class DriftSparks {
  /** Root group -- add this to the kart's parent (scene), NOT the kart group,
   *  because we want sparks to stay where they were spawned in world space. */
  readonly worldGroup: THREE.Group;

  /** Streak planes that live inside the kart group (move with the kart) */
  readonly kartGroup: THREE.Group;

  private geometry: THREE.BufferGeometry;
  private material: THREE.ShaderMaterial;
  private points: THREE.Points;

  // Per-particle CPU state
  private sparks: Spark[] = [];

  // Buffer attributes
  private positionAttr: THREE.BufferAttribute;
  private colorAttr: THREE.BufferAttribute;
  private opacityAttr: THREE.BufferAttribute;
  private sizeAttr: THREE.BufferAttribute;

  // Ground streak meshes (left and right) -- inner bright layer
  private streakLeft: THREE.Mesh;
  private streakRight: THREE.Mesh;

  // Ground streak meshes -- outer glow layer (wider, lower opacity, higher emissive)
  private glowLeft: THREE.Mesh;
  private glowRight: THREE.Mesh;

  // Spawn accumulator (fractional spark spawning)
  private spawnAccum = 0;

  // Rear wheel offsets (local kart space, +X = forward)
  private rearLeftLocal: THREE.Vector3;
  private rearRightLocal: THREE.Vector3;

  constructor(
    rearLeftOffset: { x: number; y: number; z: number },
    rearRightOffset: { x: number; y: number; z: number }
  ) {
    this.rearLeftLocal = new THREE.Vector3(rearLeftOffset.x, rearLeftOffset.y, rearLeftOffset.z);
    this.rearRightLocal = new THREE.Vector3(rearRightOffset.x, rearRightOffset.y, rearRightOffset.z);

    // ── Points geometry ──
    this.geometry = new THREE.BufferGeometry();

    const positions = new Float32Array(MAX_PARTICLES * 3);
    const colors    = new Float32Array(MAX_PARTICLES * 3);
    const opacities = new Float32Array(MAX_PARTICLES);
    const sizes     = new Float32Array(MAX_PARTICLES);

    this.positionAttr = new THREE.BufferAttribute(positions, 3);
    this.colorAttr    = new THREE.BufferAttribute(colors,    3);
    this.opacityAttr  = new THREE.BufferAttribute(opacities, 1);
    this.sizeAttr     = new THREE.BufferAttribute(sizes,     1);

    this.positionAttr.setUsage(THREE.DynamicDrawUsage);
    this.colorAttr.setUsage(THREE.DynamicDrawUsage);
    this.opacityAttr.setUsage(THREE.DynamicDrawUsage);
    this.sizeAttr.setUsage(THREE.DynamicDrawUsage);

    this.geometry.setAttribute('position', this.positionAttr);
    this.geometry.setAttribute('aColor',   this.colorAttr);
    this.geometry.setAttribute('aOpacity', this.opacityAttr);
    this.geometry.setAttribute('size',     this.sizeAttr);

    // Draw count starts at 0
    this.geometry.setDrawRange(0, 0);

    // ── Shader material: elongated streak with bright HDR core + soft glow ──
    this.material = new THREE.ShaderMaterial({
      uniforms: {},
      vertexShader: /* glsl */ `
        attribute vec3 aColor;
        attribute float aOpacity;
        attribute float size;
        varying vec3 vColor;
        varying float vOpacity;

        void main() {
          vColor   = aColor;
          vOpacity = aOpacity;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          // Raised constant (300) and raised max clamp (64) for more visual spread
          gl_PointSize = size * (300.0 / -mvPosition.z);
          gl_PointSize = clamp(gl_PointSize, 2.0, 64.0);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        varying vec3 vColor;
        varying float vOpacity;

        void main() {
          vec2 uv = gl_PointCoord - 0.5;

          // Elongated ellipse in the Y direction to simulate motion-blur streak
          // X is compressed (elongate factor widens the ellipse check in X)
          float elongate = 2.5;
          vec2 uvScaled = vec2(uv.x * elongate, uv.y);
          float dist = length(uvScaled);
          if (dist > 0.5) discard;

          // Bright HDR core -- tight central dot
          float core = 1.0 - smoothstep(0.0, 0.15, dist);
          // Soft glow halo -- wide falloff
          float glow = 1.0 - smoothstep(0.1, 0.5, dist);
          float alpha = (core * 0.9 + glow * 0.4) * vOpacity;

          // HDR brightness boost on the core for bloom pickup
          vec3 color = vColor * (1.0 + core * 2.0);

          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;

    this.worldGroup = new THREE.Group();
    this.worldGroup.add(this.points);

    // ── Ground streak planes (attach to kart group so they move with the kart) ──
    // Each wheel gets two overlapping planes:
    //   - inner:  original size, higher opacity, tiered emissiveIntensity
    //   - outer glow: 1.5x wider, lower opacity, even higher emissive for bloom

    const innerGeo = new THREE.PlaneGeometry(1.0, 0.35);
    innerGeo.rotateX(-Math.PI / 2);

    const outerGeo = new THREE.PlaneGeometry(1.5, 0.5);
    outerGeo.rotateX(-Math.PI / 2);

    // Inner streak materials (per-mesh clone so left/right can differ if needed)
    const innerMatBase = new THREE.MeshStandardMaterial({
      color: STREAK_ORANGE,
      emissive: STREAK_ORANGE,
      emissiveIntensity: 2.0,
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    // Outer glow materials -- wider, dimmer base opacity, extra emissive
    const outerMatBase = new THREE.MeshStandardMaterial({
      color: STREAK_ORANGE,
      emissive: STREAK_ORANGE,
      emissiveIntensity: 3.5,
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    this.streakLeft  = new THREE.Mesh(innerGeo, innerMatBase.clone());
    this.streakRight = new THREE.Mesh(innerGeo, innerMatBase.clone());
    this.glowLeft    = new THREE.Mesh(outerGeo, outerMatBase.clone());
    this.glowRight   = new THREE.Mesh(outerGeo, outerMatBase.clone());

    this.streakLeft.position.set(rearLeftOffset.x,  0.02, rearLeftOffset.z);
    this.streakRight.position.set(rearRightOffset.x, 0.02, rearRightOffset.z);
    // Glow planes sit fractionally higher to avoid z-fighting with the inner streak
    this.glowLeft.position.set(rearLeftOffset.x,  0.015, rearLeftOffset.z);
    this.glowRight.position.set(rearRightOffset.x, 0.015, rearRightOffset.z);

    this.kartGroup = new THREE.Group();
    this.kartGroup.add(this.streakLeft,  this.streakRight);
    this.kartGroup.add(this.glowLeft,    this.glowRight);

    // Dispose the base templates -- the clones are what get added to the scene
    innerMatBase.dispose();
    outerMatBase.dispose();
  }

  /**
   * Update the drift spark system.
   *
   * @param dt Delta time in seconds
   * @param isDrifting Whether the kart is currently drifting
   * @param driftDuration How long the current drift has lasted (seconds)
   * @param speed Current kart speed
   * @param kartWorldPos World position of the kart group
   * @param kartYaw Yaw angle of the kart (radians)
   */
  update(
    dt: number,
    isDrifting: boolean,
    driftDuration: number,
    speed: number,
    kartWorldPos: THREE.Vector3,
    kartYaw: number
  ): void {
    // Determine the spark color based on drift duration
    const sparkColor = this.getDriftColor(driftDuration);

    // ── Spawn new sparks ──
    if (isDrifting && Math.abs(speed) > 3) {
      this.spawnAccum += SPAWN_RATE * dt;

      while (this.spawnAccum >= 1 && this.sparks.length < MAX_PARTICLES) {
        this.spawnAccum -= 1;
        this.spawnSpark(kartWorldPos, kartYaw, sparkColor);
      }

      // If buffer full, cap accumulator
      if (this.sparks.length >= MAX_PARTICLES) {
        this.spawnAccum = 0;
      }
    } else {
      this.spawnAccum = 0;
    }

    // ── Update existing sparks ──
    const posArr = this.positionAttr.array as Float32Array;
    const colArr = this.colorAttr.array as Float32Array;

    let writeIdx = 0;
    for (let i = 0; i < this.sparks.length; i++) {
      const s = this.sparks[i];
      s.age += dt;

      if (s.age >= s.lifetime) {
        // Dead spark -- skip (compact array)
        continue;
      }

      const srcBase = i * 3;
      const dstBase = writeIdx * 3;

      // If compacting, copy position and color data from source to dest first
      if (writeIdx !== i) {
        posArr[dstBase]     = posArr[srcBase];
        posArr[dstBase + 1] = posArr[srcBase + 1];
        posArr[dstBase + 2] = posArr[srcBase + 2];

        colArr[dstBase]     = colArr[srcBase];
        colArr[dstBase + 1] = colArr[srcBase + 1];
        colArr[dstBase + 2] = colArr[srcBase + 2];
      }

      // Gravity pulls sparks down
      s.vy -= 12.0 * dt;

      // Apply velocity to position
      posArr[dstBase]     += s.vx * dt;
      posArr[dstBase + 1] += s.vy * dt;
      posArr[dstBase + 2] += s.vz * dt;

      // Clamp Y to ground (sparks bounce/die at ground level)
      if (posArr[dstBase + 1] < 0.01) {
        posArr[dstBase + 1] = 0.01;
        s.vy = Math.abs(s.vy) * 0.3; // small bounce
      }

      // Fade opacity based on age
      const t = s.age / s.lifetime;
      const opacity = 1.0 - t * t; // quadratic fade out
      this.opacityAttr.array[writeIdx] = opacity;

      // Size shrinks slightly over lifetime -- start larger than before for more spread
      this.sizeAttr.array[writeIdx] = THREE.MathUtils.lerp(4.5, 0.5, t);

      this.sparks[writeIdx] = s;
      writeIdx++;
    }

    // Trim the sparks array to only living particles
    this.sparks.length = writeIdx;

    // Update draw range and flag attributes dirty
    this.geometry.setDrawRange(0, writeIdx);
    this.positionAttr.needsUpdate = true;
    this.colorAttr.needsUpdate    = true;
    this.opacityAttr.needsUpdate  = true;
    this.sizeAttr.needsUpdate     = true;

    // ── Update ground streaks ──
    this.updateStreaks(isDrifting, driftDuration, speed);
  }

  /** Get the HDR color for the current drift tier (used for spark particles) */
  private getDriftColor(driftDuration: number): THREE.Color {
    if (driftDuration >= RED_THRESHOLD)    return DRIFT_PURPLE;
    if (driftDuration >= ORANGE_THRESHOLD) return DRIFT_RED;
    return DRIFT_ORANGE;
  }

  /** Get the LDR streak color for the current drift tier (used for MeshStandardMaterial) */
  private getStreakColor(driftDuration: number): THREE.Color {
    if (driftDuration >= RED_THRESHOLD)    return STREAK_PURPLE;
    if (driftDuration >= ORANGE_THRESHOLD) return STREAK_RED;
    return STREAK_ORANGE;
  }

  /** Spawn a single spark at one of the rear wheel world positions */
  private spawnSpark(
    kartWorldPos: THREE.Vector3,
    kartYaw: number,
    color: THREE.Color
  ): void {
    // Pick a random rear wheel
    const localOffset = Math.random() < 0.5 ? this.rearLeftLocal : this.rearRightLocal;

    // Transform local offset to world space
    // kart group rotation.y = -yaw, so world transform uses -yaw
    const cosY = Math.cos(-kartYaw);
    const sinY = Math.sin(-kartYaw);
    const wx = kartWorldPos.x + localOffset.x * cosY - localOffset.z * sinY;
    const wy = localOffset.y * 0.5; // Spawn slightly above ground
    const wz = kartWorldPos.z + localOffset.x * sinY + localOffset.z * cosY;

    // Random velocity: sparks fly outward/backward/upward with jitter
    // "Backward" in kart space is -X, but in world it rotates with yaw
    const backX = -Math.cos(-kartYaw);
    const backZ = -Math.sin(-kartYaw);

    const spreadX = (Math.random() - 0.5) * 4.0;
    const spreadZ = (Math.random() - 0.5) * 4.0;
    const spreadY = Math.random() * 3.0 + 1.0;

    const spark: Spark = {
      age: 0,
      lifetime: SPARK_LIFETIME_MIN + Math.random() * (SPARK_LIFETIME_MAX - SPARK_LIFETIME_MIN),
      vx: backX * 2.0 + spreadX,
      vy: spreadY,
      vz: backZ * 2.0 + spreadZ,
    };

    const idx = this.sparks.length;
    this.sparks.push(spark);

    // Set initial position in the buffer
    const base3 = idx * 3;
    (this.positionAttr.array as Float32Array)[base3]     = wx;
    (this.positionAttr.array as Float32Array)[base3 + 1] = wy;
    (this.positionAttr.array as Float32Array)[base3 + 2] = wz;

    // Set color
    (this.colorAttr.array as Float32Array)[base3]     = color.r;
    (this.colorAttr.array as Float32Array)[base3 + 1] = color.g;
    (this.colorAttr.array as Float32Array)[base3 + 2] = color.b;

    // Initial opacity and size
    this.opacityAttr.array[idx] = 1.0;
    this.sizeAttr.array[idx]    = 4.5;
  }

  /** Animate the ground streak planes under the rear wheels */
  private updateStreaks(isDrifting: boolean, driftDuration: number, speed: number): void {
    const isActive = isDrifting && Math.abs(speed) > 3;

    // Inner streak: opacity ramps to 0.75 while active
    const innerTargetOpacity = isActive ? 0.75 : 0;
    // Outer glow: slightly lower opacity so it reads as a soft aura
    const outerTargetOpacity = isActive ? 0.45 : 0;

    // Drift-tier based emissiveIntensity for the inner streak
    const streakIntensity = isActive
      ? (driftDuration < ORANGE_THRESHOLD ? 2.0
        : driftDuration < RED_THRESHOLD   ? 3.0
        : 4.5)
      : 0;

    // Outer glow is always brighter than inner for bloom
    const glowIntensity = isActive
      ? (driftDuration < ORANGE_THRESHOLD ? 3.5
        : driftDuration < RED_THRESHOLD   ? 5.0
        : 7.0)
      : 0;

    const streakColor = this.getStreakColor(driftDuration);

    // Quick-pulse multiplier shared across both layers while drifting
    const pulse = isActive
      ? 1.0 + Math.sin(performance.now() * 0.015) * 0.15
      : 1.0;
    const flicker = isActive
      ? streakIntensity * (0.85 + Math.sin(performance.now() * 0.02) * 0.15)
      : 0;
    const glowFlicker = isActive
      ? glowIntensity * (0.85 + Math.sin(performance.now() * 0.02 + 0.5) * 0.15)
      : 0;

    // ── Inner streaks ──
    for (const streak of [this.streakLeft, this.streakRight]) {
      const mat = streak.material as THREE.MeshStandardMaterial;

      mat.opacity += (innerTargetOpacity - mat.opacity) * 0.15;
      if (mat.opacity < 0.01) mat.opacity = 0;

      mat.color.copy(streakColor);
      mat.emissive.copy(streakColor);
      mat.emissiveIntensity = flicker;

      if (isActive) {
        streak.scale.set(pulse, 1, pulse);
      } else {
        streak.scale.set(1, 1, 1);
      }
    }

    // ── Outer glow streaks ──
    for (const glow of [this.glowLeft, this.glowRight]) {
      const mat = glow.material as THREE.MeshStandardMaterial;

      mat.opacity += (outerTargetOpacity - mat.opacity) * 0.12; // slightly slower fade
      if (mat.opacity < 0.01) mat.opacity = 0;

      mat.color.copy(streakColor);
      mat.emissive.copy(streakColor);
      mat.emissiveIntensity = glowFlicker;

      if (isActive) {
        glow.scale.set(pulse * 1.05, 1, pulse * 1.05);
      } else {
        glow.scale.set(1, 1, 1);
      }
    }
  }

  /** Clean up all Three.js resources */
  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();

    (this.streakLeft.material  as THREE.MeshStandardMaterial).dispose();
    (this.streakRight.material as THREE.MeshStandardMaterial).dispose();
    (this.glowLeft.material    as THREE.MeshStandardMaterial).dispose();
    (this.glowRight.material   as THREE.MeshStandardMaterial).dispose();

    // streakLeft and streakRight share the inner geometry instance;
    // glowLeft and glowRight share the outer geometry instance.
    // Dispose each geometry only once.
    (this.streakLeft.geometry  as THREE.BufferGeometry).dispose();
    (this.glowLeft.geometry    as THREE.BufferGeometry).dispose();
  }
}
